import { Directive, OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import {
  EmailActions,
  EmailAdminFilterKey,
  EmailStatus,
  EmailSummaryDto,
  EmailTag,
} from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import {
  emailActionDisplayTransform,
  emailStatusDisplayTransform,
  emailTagDisplayTransform,
} from '@workspace/ng-data-access';
import { Observable } from 'rxjs';

export type EmailColumnKey =
  'context' | 'to' | 'tag' | 'action' | 'status' | 'sendDate' | 'updatedAt' | 'createdAt';

@Directive()
export abstract class AbstractEmailsDataTableCoreComponent<T extends EmailSummaryDto>
  extends BaseDataTableComponent<T, EmailColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<EmailColumnKey>[]>([
    { key: 'context', label: 'Contexte', display: true },
    { key: 'to', label: 'À', display: true },
    { key: 'tag', label: 'Tag', display: false },
    { key: 'action', label: 'Action', display: true },
    { key: 'status', label: 'Status', display: true },
    { key: 'sendDate', label: 'Envoyé le', display: false },
    { key: 'updatedAt', label: 'Date de modification', display: false },
    { key: 'createdAt', label: 'Date de création', display: false },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'open_in_new',
      onClick: (email: EmailSummaryDto) => this.navigateToEmail(email),
      disabled: false,
      color: 'primary',
    },
  ]);

  override filters = signal<DataHeaderFilter<EmailAdminFilterKey>[]>([
    {
      label: 'Actions',
      column: EmailAdminFilterKey.Action,
    },
    {
      label: 'Tags',
      column: EmailAdminFilterKey.Tag,
    },
    {
      label: 'Status',
      column: EmailAdminFilterKey.Status,
    },
  ]);

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const action = params.getAll(EmailAdminFilterKey.Action);
      const tag = params.getAll(EmailAdminFilterKey.Tag);
      const status = params.getAll(EmailAdminFilterKey.Status);
      this.filterChanged({
        changed: true,
        filter: {
          action: action.length ? action : [],
          tag: tag.length ? tag : [],
          status: status.length ? status : [],
        },
      });
    });
  }

  navigateToEmail(email: EmailSummaryDto): void {
    void this.router.navigate(['/emails', email._id]);
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;
  override getDisplayForEntry(key: EmailAdminFilterKey, rawValue: string): string {
    switch (key) {
      case EmailAdminFilterKey.Action:
        return emailActionDisplayTransform(rawValue as EmailActions);
      case EmailAdminFilterKey.Tag:
        return emailTagDisplayTransform(rawValue as EmailTag);
      case EmailAdminFilterKey.Status:
        return emailStatusDisplayTransform(rawValue as EmailStatus);
      default:
        return key;
    }
  }
}
